import { type FC, useEffect, useState } from "react";

import { Page, Section, Spinner, Tabs, Text } from "@alpacahq/ui/old";

import { useGetCurrentAccountIfLiveElseTradingAccount } from "../../../v2/api/hooks";
import {
  getOAuthClients,
  getOAuthConnectedClients,
  getOAuthCreatedClients,
  type OAuthClientResponse,
} from "../../api/oauth";
import { AppPageGrid } from "./AppPageGrid";

// Apps ordered in reverse
const APP_ORDER = {
  featured: ["SignalStack", "TradersPost", "TradingView"],
  all: [
    "Trellis",
    "Feather Finance",
    "BLSH",
    "Aramche",
    "Muslim Xchange",
    "Zoya Finance",
    "StockHero",
    "QuantMage",
    "Machinetrader",
    "Breaking Equity",
    "CandleX",
    "AlgoBulls",
    "SignalStack",
    "TradersPost",
    "TradingView",
  ],
};

const sortApps = (apps: OAuthClientResponse[], order: string[]) =>
  apps.slice().sort((a, b) => {
    const aIndex = order.indexOf(a.name);
    const bIndex = order.indexOf(b.name);
    return bIndex - aIndex;
  });

export const AppPage: FC = () => {
  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();

  // initialize apps state with null
  const [apps, setApps] = useState<{
    featured: OAuthClientResponse[];
    my_developed_apps: (OAuthClientResponse | { placeholder: boolean })[];
    my_connected_apps: OAuthClientResponse[];
    all: OAuthClientResponse[];
  } | null>(null);

  useEffect(() => {
    // if there's no account data, don't attempt to fetch apps
    if (!account?.id) {
      return;
    }

    const fetchApps = async () => {
      // fetch all the app categories in parallel
      const [featuredApps, createdApps, connectedApps, allApps] =
        await Promise.all([
          getOAuthClients({ public: true, featured: true }),
          getOAuthCreatedClients(),
          getOAuthConnectedClients(account.id),
          getOAuthClients({ public: true }),
        ]);

      // filter out the apps from "all" category that are already in the other categories
      const uniqueAllApps = allApps.filter(
        (app) =>
          ![...featuredApps].find(
            (catApp) => catApp.client_id === app.client_id,
          ),
      );

      // update the state with the fetched data
      setApps({
        featured: sortApps(featuredApps, APP_ORDER.featured),
        my_developed_apps: [...createdApps, { placeholder: true }],
        my_connected_apps: connectedApps,
        all: sortApps(uniqueAllApps, APP_ORDER.all),
      });
    };

    fetchApps();
  }, [account]);

  return (
    <Page
      title="Alpaca Connect"
      subtitle="Integrate apps built by the community."
      className="p-8 pt-0 xs:pl-0 md:p-8 md:pt-0 md:pl-0"
    >
      {!apps ? (
        <Spinner className="mx-auto" />
      ) : (
        <Tabs>
          <Tabs.Panel name="Marketplace">
            <Section title="Featured">
              <AppPageGrid apps={apps.featured} />
            </Section>
            {apps.all.length > 0 && (
              <Section title="All Apps">
                <AppPageGrid apps={apps.all} />
              </Section>
            )}
          </Tabs.Panel>
          <Tabs.Panel name="Connected Apps">
            <AppPageGrid apps={apps.my_connected_apps} />
            {apps.my_connected_apps.length === 0 && (
              <Text className="text-gray-500 text-md">
                You have not connected any apps yet.
              </Text>
            )}
          </Tabs.Panel>
          <Tabs.Panel name="My Developed Apps">
            <AppPageGrid apps={apps.my_developed_apps} />
          </Tabs.Panel>
        </Tabs>
      )}
    </Page>
  );
};

export default AppPage;
